/**
 * Хуки данных экрана уборщицы «Уборка» (процесс выселения, housekeeping wave).
 * Server-state только через React Query (патент проекта).
 *
 * Цикл выезда (бэк internal/room, FSM номера):
 *   выезд → pending_inspection «На проверке»  (stay→room side effect)
 *   «Номер проверен»  → cleaning «На уборке»   (горничная)
 *   «Закрыть заявку»  → available «Свободен»    (горничная)
 *   «В ремонт»        → repair «Ремонт»          (найдена поломка)
 *
 * Очередь горничной = заявки «Выселение»/cleaning + номера «На проверке».
 * Грузим обе фазы (pending_inspection + cleaning) и показываем единым списком
 * с разными действиями. Отдельного экрана «отправить на уборку» больше нет.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  closeCleaningRequest,
  getPublicRoomTypes,
  listRooms,
  markRoomInspected,
  sendRoomToRepair,
} from '@hsc/api-client';
import type {
  PublicRoomTypesResponse,
  RoomListResponse,
  RoomView,
} from '@hsc/types';

/**
 * Очередь уборки: номера «На проверке» (pending_inspection) + «На уборке»
 * (cleaning). Два запроса объединяются клиентом — бэк фильтрует по одному
 * статусу за раз.
 */
export function useCleaningQueue() {
  const pending = useQuery<RoomListResponse>({
    queryKey: ['rooms', { status: 'pending_inspection' }],
    queryFn: () => listRooms({ status: 'pending_inspection' }),
    refetchInterval: 30_000,
  });
  const cleaning = useQuery<RoomListResponse>({
    queryKey: ['rooms', { status: 'cleaning' }],
    queryFn: () => listRooms({ status: 'cleaning' }),
    refetchInterval: 30_000,
  });
  const items: RoomView[] = [
    ...(pending.data?.items ?? []),
    ...(cleaning.data?.items ?? []),
  ];
  return {
    items,
    isLoading: pending.isLoading || cleaning.isLoading,
    isError: pending.isError || cleaning.isError,
    hasData: pending.data !== undefined && cleaning.data !== undefined,
  };
}

/** Имена типов номеров (для подписи в карточке) — публичный каталог по slug. */
export function useRoomTypeNames(slug: string | null) {
  return useQuery<PublicRoomTypesResponse>({
    queryKey: ['room-types', slug],
    queryFn: () => getPublicRoomTypes(slug as string),
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  });
}

/** Действие «Номер проверен»: pending_inspection → cleaning. */
export function useMarkInspected() {
  const qc = useQueryClient();
  return useMutation<RoomView, Error, { id: string }>({
    mutationFn: ({ id }) => markRoomInspected(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}

/** Действие «Закрыть заявку»: cleaning → available. */
export function useCloseCleaning() {
  const qc = useQueryClient();
  return useMutation<RoomView, Error, { id: string }>({
    mutationFn: ({ id }) => closeCleaningRequest(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}

/** Действие «В ремонт»: pending_inspection → repair. */
export function useSendToRepair() {
  const qc = useQueryClient();
  return useMutation<RoomView, Error, { id: string }>({
    mutationFn: ({ id }) => sendRoomToRepair(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rooms'] }),
  });
}
